// Product Hunt launch offer: 50% off the first 3 months with code PRODUCTHUNT.
//
// A visitor who arrives from Product Hunt (`?ref=producthunt`, which is what
// PH's "Visit website" button appends, or `?utm_source=producthunt`, or the
// /producthunt landing itself) gets a first-party cookie that outlives the
// landing page, so the offer banner follows them to /pricing and the discount
// is applied automatically when they reach Stripe Checkout.
//
// The offer ends on its own: every read checks PRODUCT_HUNT_OFFER_ENDS_AT, and
// the cookie expires at the same instant, so nothing needs a redeploy to turn
// it off. The backend enforces the same date independently
// (packages/services/billing/launch_promotion.py) — keep the two in sync.
//
// Everything here is best-effort and never throws: an offer banner must not be
// able to break a page or a checkout.

import { resolveCookieDomain } from '@/lib/analytics/track'

export const PRODUCT_HUNT_PROMO_CODE = 'PRODUCTHUNT'

/** Offer key sent to `POST /api/billing/checkout` as `promo`. */
export const PRODUCT_HUNT_PROMO_KEY = 'producthunt'

/** End of 2026-11-07 in Pacific time (PST, UTC-8) — Product Hunt's clock. */
export const PRODUCT_HUNT_OFFER_ENDS_AT = Date.parse('2026-11-08T08:00:00Z')

const OFFER_COOKIE = 'soku_ph_offer'
const DISMISSED_COOKIE = 'soku_ph_offer_dismissed'

export function isProductHuntOfferLive(now: number = Date.now()): boolean {
  return now < PRODUCT_HUNT_OFFER_ENDS_AT
}

/** Whether this URL is a Product Hunt arrival. */
export function isProductHuntArrival(
  pathname: string,
  search: string,
): boolean {
  if (pathname === '/producthunt' || pathname.startsWith('/producthunt/')) {
    return true
  }
  const params = new URLSearchParams(
    search.startsWith('?') ? search : `?${search}`,
  )
  return ['ref', 'utm_source'].some(
    key => params.get(key)?.trim().toLowerCase() === PRODUCT_HUNT_PROMO_KEY,
  )
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return match ? match[1] : undefined
  } catch {
    return undefined
  }
}

function writeCookie(name: string, now: number): void {
  try {
    const maxAge = Math.floor((PRODUCT_HUNT_OFFER_ENDS_AT - now) / 1000)
    if (maxAge <= 0) return
    const domain = resolveCookieDomain(window.location.hostname)
    const domainAttr = domain ? `; Domain=${domain}` : ''
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${name}=1; Max-Age=${maxAge}; Path=/; SameSite=Lax${domainAttr}${secure}`
  } catch {
    // Cookies can be blocked by privacy settings; the offer is optional.
  }
}

/**
 * Flag this browser for the offer when the current URL is a Product Hunt
 * arrival. Safe to call on every page view; a no-op once the offer has ended.
 */
export function captureProductHuntOffer(now: number = Date.now()): void {
  if (typeof window === 'undefined' || !isProductHuntOfferLive(now)) return
  try {
    if (
      isProductHuntArrival(window.location.pathname, window.location.search)
    ) {
      writeCookie(OFFER_COOKIE, now)
    }
  } catch {
    // Best-effort only.
  }
}

/** Whether this browser came from Product Hunt and the offer is still live. */
export function hasProductHuntOffer(now: number = Date.now()): boolean {
  return isProductHuntOfferLive(now) && readCookie(OFFER_COOKIE) === '1'
}

export function isProductHuntBannerDismissed(): boolean {
  return readCookie(DISMISSED_COOKIE) === '1'
}

/** Hide the banner for the rest of the offer. Checkout auto-apply is kept. */
export function dismissProductHuntBanner(now: number = Date.now()): void {
  if (typeof window === 'undefined') return
  writeCookie(DISMISSED_COOKIE, now)
}

// /producthunt already presents the offer as its hero; a bar repeating it
// above that would be noise.
const BANNER_HIDDEN_ON = ['/producthunt']

/**
 * Whether the offer bar should show on `pathname`. Client-only: it reads the
 * cookie and, on the landing render (before the cookie is written), the URL.
 * Other homepage announcement bars use it to step aside for the offer.
 */
export function shouldShowProductHuntBanner(
  pathname: string,
  now: number = Date.now(),
): boolean {
  if (typeof window === 'undefined' || BANNER_HIDDEN_ON.includes(pathname)) {
    return false
  }
  const flagged =
    hasProductHuntOffer(now) ||
    (isProductHuntOfferLive(now) &&
      isProductHuntArrival(window.location.pathname, window.location.search))
  return flagged && !isProductHuntBannerDismissed()
}
